import {
  ReactNode,
  Dispatch,
  useReducer,
  createContext,
  useContext,
} from "react";

import { themeMixins } from "../constants/ThemeMixins";

import { FullscreenLoading } from "../components/shared/FullscreenLoading";

import { IBrand } from "../models/Brand/Brand";

const themeColorsLsKey = "theme-colors";

export interface ColorValue {
  r: number;
  g: number;
  b: number;
}

interface ITheme {
  loading: boolean;
  primary: ColorValue;
  secondary: ColorValue;
  primaryContrast: ColorValue;
  secondaryContrast: ColorValue;
  logo?: string;
}

const initialState: ITheme = {
  loading: false,
  primary: { r: 0, g: 143, b: 155 },
  secondary: { r: 155, g: 0, b: 133 },
  primaryContrast: { r: 0, g: 0, b: 0 },
  secondaryContrast: { r: 0, g: 0, b: 0 },
  logo: undefined,
};

interface ThemeContextValue {
  theme: ITheme;
  setTheme: Dispatch<ThemeReducerAction>;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: initialState,
  setTheme: () => {},
});

interface ThemeContextProviderProps {
  children: ReactNode | ReactNode[];
}

type ThemeReducerAction =
  | {
      type: "loading";
      payload: boolean;
    }
  | {
      type: "colors";
      payload: {
        primary: ColorValue;
        secondary: ColorValue;
      };
    }
  | {
      type: "logo";
      payload: string;
    }
  | {
      type: "reset";
    };

function getContrastColor({ r, g, b }: ColorValue): ColorValue {
  const brightness = Math.round((r * 299 + g * 587 + b * 114) / 1000);
  return brightness > 125 ? { r: 0, g: 0, b: 0 } : { r: 255, g: 255, b: 255 };
}

function findThemeCSS() {
  const sheets = document.styleSheets;

  for (let i = 0; i < sheets.length; i++) {
    const sheet = sheets.item(i);
    if (sheet?.href?.includes("theme.css")) {
      return sheet;
    }
  }

  return undefined;
}

function setThemeCSS(
  primary: ColorValue,
  secondary: ColorValue,
  primaryContrast: ColorValue,
  secondaryContrast: ColorValue
) {
  // Construct new css strings
  let cssText = `:root { --color-primary: ${primary.r} ${primary.g} ${primary.b}; --color-secondary: ${secondary.r} ${secondary.g} ${secondary.b}; --color-primaryContrast: ${primaryContrast.r} ${primaryContrast.g} ${primaryContrast.b}; --color-secondaryContrast: ${secondaryContrast.r} ${secondaryContrast.g} ${secondaryContrast.b}; ${themeMixins} }`;

  // Get the <style> tag in the head
  let style: CSSStyleSheet | null | undefined;

  style = findThemeCSS();

  let numRules = style!.cssRules.length;

  let index;

  // Find the index of the rule that contains the defs
  for (let i = 0; i < numRules; i++) {
    let item = style!.cssRules.item(i);
    if (item?.cssText.includes(":root ")) {
      index = i;
    }
  }

  // If we found them, remove them and add the new rules
  if (index !== undefined) {
    style!.deleteRule(index);
    style!.insertRule(cssText);
  }
}

function themeReducer(state: ITheme, action: ThemeReducerAction): ITheme {
  switch (action.type) {
    case "loading":
      return { ...state, loading: action.payload };
    case "colors":
      let lpc = getContrastColor(action.payload.primary);
      let lsc = getContrastColor(action.payload.secondary);

      setThemeCSS(action.payload.primary, action.payload.secondary, lpc, lsc);

      let finalPayload = {
        light: {
          ...action.payload,
          primaryContrast: lpc,
          secondaryContrast: lsc,
        },
      };

      localStorage.setItem(themeColorsLsKey, JSON.stringify(finalPayload));

      return { ...state, ...finalPayload };
    case "logo":
      return { ...state, logo: action.payload };
    case "reset":
      setThemeCSS(
        initialState.primary,
        initialState.secondary,
        initialState.primaryContrast,
        initialState.secondaryContrast
      );

      localStorage.setItem(themeColorsLsKey, JSON.stringify({ initialState }));

      return { ...initialState, loading: false };
    default:
      return state;
  }
}

export function ThemeContextProvider({ children }: ThemeContextProviderProps) {
  const [theme, setTheme] = useReducer(themeReducer, initialState);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {theme.loading ? <FullscreenLoading /> : children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const themeCtx = useContext(ThemeContext);

  if (!themeCtx) {
    throw Error("useTheme() must be used within a ThemeContextProvider");
  }

  const setTheme = (brand: IBrand) => {
    themeCtx.setTheme({ type: "loading", payload: true });
    let primary = brand.primaryColour;
    let secondary = brand.secondaryColour;

    themeCtx.setTheme({
      type: "colors",
      payload: {
        primary: {
          r: primary[0],
          g: primary[1],
          b: primary[2],
        },
        secondary: {
          r: secondary[0],
          g: secondary[1],
          b: secondary[2],
        },
      },
    });

    themeCtx.setTheme({ type: "logo", payload: brand.logo ?? "" });

    themeCtx.setTheme({ type: "loading", payload: false });
  };

  return { theme: themeCtx.theme, setTheme: setTheme };
}
